/* eslint-disable no-useless-escape */
const VIRTUE_COMPLETED_POSTFIX = '_completed';
const VIRTUE_COMPLETED_PREFIX = 'virtue_';

export function getUrlParam(parameter, defaultvalue) {
  let urlparameter = defaultvalue;
  if (window.location.href.indexOf(parameter) > -1) {
    const vars = {};

    window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, (
      m,
      key,
      value,
    ) => {
      vars[key] = value;
    });

    urlparameter = vars[parameter];
  }

  return urlparameter;
}

export function stripHtml(html, length = false, end = '...') {
  const temporalDivElement = document.createElement('div');
  temporalDivElement.innerHTML = html;
  let text = temporalDivElement.textContent || temporalDivElement.innerText || '';

  if (length !== false && typeof length === 'number' && text.length > length) {
    text = text.substr(0, length);
    text = text.substr(0, Math.min(text.length, text.lastIndexOf(' '))) + end;
  }

  return text;
}

/**
 * Creates a structured slug from the parameter `text`
 * @param {string} text Text that will be replaced
 */
export function slugify(text) {
  if (!text) {
    return null;
  }

  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

/**
 * Spreads the completed keys and returns it as an array.
 * @param {object} userMeta The user's acf propery
 * @param {boolean} returnLength Returns the length of the lessonKeys array
 */
export function getCompletedVirtueLessonsByKey(
  userMeta,
  returnLength = false,
  currentVirtueId = null,
) {
  if (!userMeta) {
    return null;
  }
  const lessonKeys = [];
  Object.keys(userMeta).forEach((key) => {
    if (currentVirtueId) {
      if (
        key.endsWith(VIRTUE_COMPLETED_POSTFIX)
        && userMeta[key]
        && key.startsWith(`${VIRTUE_COMPLETED_PREFIX}${currentVirtueId}`)
      ) {
        lessonKeys.push(key);
      }
    } else if (key.endsWith(VIRTUE_COMPLETED_POSTFIX) && userMeta[key]) {
      lessonKeys.push(key);
    }
  });

  return returnLength ? lessonKeys.length : lessonKeys;
}

/**
 * Returns a virtue (exposed from `acf` property) based on the virtue id
 * @param {string} virtueId Virtue id
 * @param {object} virtues Plain object that contains the result from the backend
 */
export function getVirtueById(virtueId, virtues) {
  if (!virtueId || !virtues) {
    return null;
  }

  let virtueById = null;

  virtues.forEach((virtue) => {
    // @note Used eqeq instead of eqeqeq because of the type mismatch (string) !== (number)
    // eslint-disable-next-line eqeqeq
    if (virtue.acf && virtue.acf.virtue_id == virtueId) {
      virtueById = virtue.acf;
    }
  });

  return virtueById;
}

/**
 * Returns the virtue object that has the last edited virtue lesson.
 * @param {object} userMeta Response object from the backend
 * @param {object} virtues Response object from the backend
 */
export function getVirtueByLastJournal(userMeta, virtues) {
  if (!userMeta.acf) {
    return null;
  }

  let journalId = null;
  const journalKeys = [];
  Object.keys(userMeta.acf).forEach((key) => {
    if (key.endsWith('journal')) {
      journalKeys.push(key);
    }
  });

  const key = journalKeys[0] || null;
  if (key) {
    journalId = key.split('_');
  }

  if (!journalId) {
    return null;
  }

  if (journalId[1]) {
    return getVirtueById(journalId[1], virtues);
  }

  return null;
}
/**
 * Returns a virtue jorunal as a response.
 * @param {object} virtue Virtue
 * @param {object} userMeta User object coming from the wordpress backend
 */
export function getVirtueJournal(virtue, userMeta) {
  if (!virtue.acf || !userMeta.acf) {
    return '';
  }

  const virtueJournalKey = `virtue_${virtue.acf.virtue_id}_journal`;
  const virtueJournalValue = userMeta.acf[virtueJournalKey];
  return virtueJournalValue || '';
}

/**
 * Calculates the current progress for a virtue.
 * @param {object} userMeta The user's acf property
 */
export function calculateVirtueProgress(userMeta) {
  if (!userMeta) {
    return null;
  }

  const numberOfTotalLessons = 7;
  let completedLessons = 0;
  const lessonKeys = getCompletedVirtueLessonsByKey(
    userMeta,
    false,
    userMeta.current_virtue,
  );

  lessonKeys.forEach((lessonKey) => {
    if (userMeta[lessonKey]) {
      completedLessons++;
    }
  });

  return Math.ceil((100 * completedLessons) / numberOfTotalLessons);
}

/**
 * Returns a random element from a non associative array.
 * @param {Array} array Non associative array `['a', 'b', 'c']`
 */
export function getRandomItemFromArray(array = []) {
  return array[Math.floor(Math.random() * array.length)];
}

/**
 * Crossmatches the user mutated data to return a boolean whether
 * the user completed the lesson or not.
 * @param {object} virtue Plain virtue object from the backend
 * @param {string} lessonId Lesson id
 * @param {object} userMeta Content of the `user` observable
 */
export function isLessonCompleted(virtue, lessonId, userMeta) {
  if (!userMeta.acf || !virtue.acf) {
    return null;
  }

  const keyProp = `virtue_${virtue.acf.virtue_id}_lesson_${Number(lessonId)
    + 1}_completed`;
  return userMeta.acf[keyProp];
}

/**
 * Returns the virtue progress postfixed field's value
 * @param {string} virtueId Current virtue id
 * @param {object} user Content of the `user` observable
 */
export function getVirtueProgressById(virtueId, user) {
  if (!virtueId || !user.acf) {
    return null;
  }

  const virtueProgress = user.acf[`virtue_${virtueId}_progress`];

  return virtueProgress || null;
}

/**
 * Returns the overall virtue progress
 * @param {object} userMeta Response from the backend
 * @param {string} output Output type: [percent, uncompleted, completed]
 */
export function getTotalVirtueProgress(userMeta, output = 'percent') {
  if (!userMeta || !userMeta.acf) {
    return null;
  }

  const numberOfTotalVirtues = 52;
  let completedVirtues = 0;
  const virtueKeys = [];
  Object.keys(userMeta.acf).forEach((key) => {
    if (
      key.endsWith(VIRTUE_COMPLETED_POSTFIX)
      && userMeta.acf[key]
      && key !== 'last_virtue_completed'
    ) {
      virtueKeys.push(key);
    }
  });

  virtueKeys.forEach((virtue) => {
    if (userMeta.acf[virtue]) {
      completedVirtues++;
    }
  });

  let virtueProgress = null;
  switch (output) {
    case 'precent':
      virtueProgress = Math.ceil(
        (100 * completedVirtues) / numberOfTotalVirtues,
      );
      break;
    case 'uncompleted':
      virtueProgress = numberOfTotalVirtues - completedVirtues;
      break;
    case 'completed':
      virtueProgress = completedVirtues;
      break;

    default:
      virtueProgress = Math.ceil(
        (100 * completedVirtues) / numberOfTotalVirtues,
      );
      break;
  }

  return virtueProgress;
}

/**
 * Checks if the current virtues is favorited
 * by the user.
 * @param {string} virtueId Virtue id
 * @param {object} userMeta Response from the backend
 */
export function isFavorited(virtue, favoriteVirtues) {
  if (!virtue || !virtue.acf || !favoriteVirtues) {
    return null;
  }

  // @note Filtered out the undefined fields
  const favorites = favoriteVirtues.filter(it => it);
  return favorites.includes(virtue.acf.virtue_id);
}

/**
 * Returns the head of the virtues array.
 * @param {object} virtues Response from the backend
 */
export function findFirstExistingVirtue(virtues) {
  if (!virtues) {
    return null;
  }

  return virtues.length ? virtues[0] : null;
}
